'use strict';

/**
 * Deterministic graph layout and topology helpers for the **PipelineStatus** DAG (React Flow view).
 * Positions follow **longest-path layering** from `dependsOn`, laid out **left → right**:
 * roots (no in-graph parents) sit in the leftmost column; nodes that still have downstream work use their depth column;
 * **leaf** stages (nothing depends on them) are grouped in the **rightmost** column so all pipeline exits align.
 * When a parent fans out to both leaves and a continuing branch, **continuation** children are shifted **down** so edges to leaves do not visually read as passing through them.
 * Edges are derived only from `dependsOn`.
 *
 * @typedef {import('../pipelineStatusTypes').PipelineStatusStage} PipelineStatusStage
 */

/** Approximate node width for horizontal spacing between columns (card ~200–240px + margin). */
const LAYOUT_NODE_WIDTH = 268;

const LAYOUT_HORIZONTAL_GAP = 40;

/** Horizontal distance between layer columns. */
const LAYOUT_COLUMN_STEP_X = LAYOUT_NODE_WIDTH + LAYOUT_HORIZONTAL_GAP;

/** Vertical spacing between nodes stacked in the same column. */
const LAYOUT_ROW_STEP_Y = 132;

/**
 * When a parent fans out to both terminal leaves and a continuing branch, shift **non-leaf** children down so
 * edges to top/right leaves do not visually read as passing through the continuation node (e.g. poller → project-available vs sample-set).
 */
const LAYOUT_FANOUT_CONTINUATION_Y_NUDGE = 140;

export const PIPELINE_STATUS_STAGE_NODE_TYPE = 'pipelineStatusStage';

/**
 * Optional manual position overrides (applied after layer layout). Prefer adjusting the graph definition order;
 * use overrides only for fine-tuning coordinates.
 *
 * @type {Readonly<Record<string, { x: number, y: number }>>}
 */
export const PIPELINE_STATUS_GRAPH_NODE_LAYOUT = Object.freeze({});

/**
 * Longest-path depth from roots: `depth(node) = 0` if no in-graph parents, else `1 + max(depth(parent))`.
 * Leaf nodes (no outgoing edges in this graph) are assigned the **maximum** depth column so all exits line up on the right.
 *
 * @param {PipelineStatusStage[]} stages
 * @returns {Map<string, { x: number, y: number }>}
 */
export function computePipelineStatusLayerPositions(stages) {
	/** @type {Map<string, { x: number, y: number }>} */
	const pos = new Map();
	if (!stages.length) return pos;

	const idSet = new Set(stages.map((s) => s.id));
	const orderIndex = new Map(stages.map((s, i) => [s.id, i]));
	const parentsOf = new Map(
		stages.map((s) => [s.id, (s.dependsOn || []).filter((d) => idSet.has(d))]),
	);
	/** @type {Map<string, number>} */
	const depthMemo = new Map();
	/** @param {string} id */
	function longestDepth(id) {
		if (depthMemo.has(id)) return /** @type {number} */ (depthMemo.get(id));
		const parents = parentsOf.get(id) || [];
		let d = 0;
		if (parents.length) d = 1 + Math.max(...parents.map(longestDepth));
		depthMemo.set(id, d);
		return d;
	}
	for (const s of stages) longestDepth(s.id);

	const maxLongestDepth = Math.max(0, ...[...depthMemo.values()]);

	const { hasOutgoing } = getPipelineStatusGraphPortSets(stages);

	/** @type {Map<number, string[]>} */
	const byColumn = new Map();
	for (const s of stages) {
		const d = longestDepth(s.id);
		const col = hasOutgoing.has(s.id) ? d : maxLongestDepth;
		if (!byColumn.has(col)) byColumn.set(col, []);
		byColumn.get(col).push(s.id);
	}
	for (const ids of byColumn.values()) {
		ids.sort((a, b) => (orderIndex.get(a) ?? 0) - (orderIndex.get(b) ?? 0));
	}

	const maxCol = byColumn.size ? Math.max(...byColumn.keys()) : 0;
	for (let c = 0; c <= maxCol; c += 1) {
		const ids = byColumn.get(c) || [];
		const n = ids.length;
		const startY = n <= 1 ? 0 : -((n - 1) * LAYOUT_ROW_STEP_Y) / 2;
		const colX = c * LAYOUT_COLUMN_STEP_X;
		for (let i = 0; i < ids.length; i += 1) {
			const id = ids[i];
			pos.set(id, {
				x: colX,
				y: startY + i * LAYOUT_ROW_STEP_Y,
			});
		}
	}

	applyMixedFanoutContinuationNudges(stages, pos, idSet, orderIndex, hasOutgoing);

	for (const [id, override] of Object.entries(PIPELINE_STATUS_GRAPH_NODE_LAYOUT)) {
		const cur = pos.get(id);
		if (cur) pos.set(id, { x: override.x, y: override.y });
	}

	return pos;
}

/**
 * If some parent has ≥2 children and the set mixes leaves with at least one child that still has downstream edges,
 * move those **continuation** children down so horizontal edges to early-ordered leaves clear them visually.
 *
 * @param {PipelineStatusStage[]} stages
 * @param {Map<string, { x: number, y: number }>} pos
 * @param {Set<string>} idSet
 * @param {Map<string, number>} orderIndex
 * @param {Set<string>} hasOutgoing
 */
function applyMixedFanoutContinuationNudges(stages, pos, idSet, orderIndex, hasOutgoing) {
	/** @type {Map<string, string[]>} */
	const childrenOf = new Map();
	for (const s of stages) {
		for (const d of s.dependsOn || []) {
			if (!idSet.has(d)) continue;
			if (!childrenOf.has(d)) childrenOf.set(d, []);
			childrenOf.get(d).push(s.id);
		}
	}
	for (const ids of childrenOf.values()) {
		ids.sort((a, b) => (orderIndex.get(a) ?? 0) - (orderIndex.get(b) ?? 0));
	}
	for (const [, childIds] of childrenOf) {
		if (childIds.length < 2) continue;
		const anyLeaf = childIds.some((cid) => !hasOutgoing.has(cid));
		const anyNonLeaf = childIds.some((cid) => hasOutgoing.has(cid));
		if (!anyLeaf || !anyNonLeaf) continue;
		for (const cid of childIds) {
			if (!hasOutgoing.has(cid)) continue;
			const p = pos.get(cid);
			if (p) pos.set(cid, { x: p.x, y: p.y + LAYOUT_FANOUT_CONTINUATION_Y_NUDGE });
		}
	}
}

/**
 * Which nodes participate in at least one rendered edge (for React Flow handle visibility).
 *
 * @param {PipelineStatusStage[]} stages
 * @returns {{ hasIncoming: Set<string>, hasOutgoing: Set<string> }}
 */
export function getPipelineStatusGraphPortSets(stages) {
	const idSet = new Set(stages.map((s) => s.id));
	const hasIncoming = new Set();
	const hasOutgoing = new Set();
	for (const s of stages) {
		for (const d of s.dependsOn || []) {
			if (!idSet.has(d) || d === s.id) continue;
			hasIncoming.add(s.id);
			hasOutgoing.add(d);
		}
	}
	return { hasIncoming, hasOutgoing };
}

/**
 * @param {string} stageId
 * @returns {{ x: number, y: number }}
 * @deprecated Positions come from {@link computePipelineStatusLayerPositions} via {@link mapPipelineStatusStagesToGraphNodes}.
 */
export function getPipelineStatusGraphNodePosition(stageId) {
	const o = PIPELINE_STATUS_GRAPH_NODE_LAYOUT[stageId];
	if (o) return { ...o };
	return { x: 0, y: 0 };
}

/**
 * @param {PipelineStatusStage[]} stages
 * @returns {{ id: string, type: string, position: { x: number, y: number }, data: { stage: PipelineStatusStage, graphHints: { hasIncoming: boolean, hasOutgoing: boolean } } }[]}
 */
export function mapPipelineStatusStagesToGraphNodes(stages) {
	const positions = computePipelineStatusLayerPositions(stages);
	const { hasIncoming, hasOutgoing } = getPipelineStatusGraphPortSets(stages);
	return stages.map((stage) => ({
		id: stage.id,
		type: PIPELINE_STATUS_STAGE_NODE_TYPE,
		position: positions.get(stage.id) ?? { x: 0, y: 0 },
		data: {
			stage,
			graphHints: {
				hasIncoming: hasIncoming.has(stage.id),
				hasOutgoing: hasOutgoing.has(stage.id),
			},
		},
	}));
}

/**
 * Directed edge dep -> stage for each entry in `stage.dependsOn`.
 * Skips unknown ids, self-loops, duplicates, and any edge that would create a directed cycle.
 *
 * @param {PipelineStatusStage[]} stages
 * @returns {{ id: string, source: string, target: string }[]}
 */
export function buildPipelineStatusGraphEdgesFromDependsOn(stages) {
	const idSet = new Set(stages.map((s) => s.id));
	/** @type {{ id: string, source: string, target: string }[]} */
	const tentative = [];
	const seen = new Set();
	for (const s of stages) {
		for (const dep of s.dependsOn || []) {
			if (!idSet.has(dep) || dep === s.id) continue;
			const id = `e:${dep}->${s.id}`;
			if (seen.has(id)) continue;
			seen.add(id);
			tentative.push({ id, source: dep, target: s.id });
		}
	}
	return filterEdgesPreservingDAG(tentative);
}

/**
 * @param {{ id: string, source: string, target: string }[]} edges
 * @returns {{ id: string, source: string, target: string }[]}
 */
function filterEdgesPreservingDAG(edges) {
	/** @type {{ id: string, source: string, target: string }[]} */
	const kept = [];
	for (const e of edges) {
		if (!wouldCreateCycle(kept, e.source, e.target)) kept.push(e);
	}
	return kept;
}

/**
 * Adding `source -> target` introduces a directed cycle iff a path already exists from `target` to `source`.
 * @param {{ source: string, target: string }[]} keptEdges
 * @param {string} source
 * @param {string} target
 */
function wouldCreateCycle(keptEdges, source, target) {
	/** @type {Map<string, string[]>} */
	const adj = new Map();
	for (const e of keptEdges) {
		if (!adj.has(e.source)) adj.set(e.source, []);
		adj.get(e.source).push(e.target);
	}
	return pathExistsInAdjacency(adj, target, source);
}

/**
 * @param {Map<string, string[]>} adj
 * @param {string} from
 * @param {string} to
 */
function pathExistsInAdjacency(adj, from, to) {
	const stack = [from];
	const seen = new Set([from]);
	while (stack.length) {
		const u = stack.pop();
		if (u === to) return true;
		for (const v of adj.get(u) || []) {
			if (!seen.has(v)) {
				seen.add(v);
				stack.push(v);
			}
		}
	}
	return false;
}
